import { useState, useMemo, useEffect, useRef, useLayoutEffect } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/auth/AuthContext";
import { Footer } from "@/components/Footer";

import {
  fetchPropertiesKeyset,
  fetchPropertyViewHeatLevel,
  getCachedViewHeatLevel,
  cacheViewHeatLevel,
  getAccessToken,
  toComboRequest,
  updateViewMode,
} from "@/lib/api";
import { HorizontalFilterBar } from "@/components/HorizontalFilterBar";
import { PropertyTable } from "@/components/PropertyTable";
import { PropertyCard } from "@/components/PropertyCard";
import { EmptyState } from "@/components/EmptyState";
import { SEO } from "@/components/SEO";
import { ReelsViewer } from "@/components/ReelsViewer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  GridFour,
  Rows,
  Table as TableIcon,
  MagnifyingGlass,
  Fire,
  Star,
  Globe,
  Spinner,
  WarningCircle,
  X,
  NotePencil,
} from "@phosphor-icons/react";
import { Film } from "lucide-react";

const PAGE_SIZE = 24;

const VIEW_MODE_TO_LAYOUT = { 0: "table", 1: "grid", 2: "list" };
const LAYOUT_TO_VIEW_MODE = { table: 0, grid: 1, list: 2 };

const TAB_TO_BACKEND_TAB = {
  web: "web",
  favorites: "favorites",
  popular: "hot",
  notes: "notes",
};

export const EMPTY_FILTERS = {
  idOwnerType: "1",
  idOperationType: "",
  idPropertyType: "",
  regionIds: "",
  metroIds: "",
  roomIds: "",
  targetIds: "",
  buildingTypeIds: "",
  repairRateIds: "",
  idDocument: "",
  priceMin: "",
  priceMax: "",
  areaMin: "",
  areaMax: "",
  landAreaMin: "",
  landAreaMax: "",
  floorMin: "",
  floorMax: "",
  buildingfloorMin: "",
  buildingfloorMax: "",
  startDate: "",
  endDate: "",
  phoneNumber: "",
  isOnlyExtract: "",
  isOnlyLoan: "",
  isNotLastFloor: "",
  keyword: "",
  target: [],
};

const getInitialLayout = () => {
  if (typeof window === "undefined") return "table";
  const stored = localStorage.getItem("viewMode");
  return VIEW_MODE_TO_LAYOUT[Number(stored)] ?? "table";
};

const HOME_STATE_KEY = "home_state_cache_v1";

const isValidFilters = (obj) =>
  obj && typeof obj === "object" && Object.keys(EMPTY_FILTERS).every((k) => k in obj);

const loadPersistedHomeState = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(HOME_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!isValidFilters(parsed.filters) || !isValidFilters(parsed.applied)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const persistedHomeState = loadPersistedHomeState();

const homeStateCache = {
  filters: persistedHomeState?.filters ? { ...persistedHomeState.filters } : { ...EMPTY_FILTERS },
  applied: persistedHomeState?.applied ? { ...persistedHomeState.applied } : { ...EMPTY_FILTERS },
  sortType: persistedHomeState?.sortType ?? "newest",
  layout: getInitialLayout(),
  tab: persistedHomeState?.tab ?? "web",
  scrollY: persistedHomeState?.scrollY ?? 0,
  loadedPages: persistedHomeState?.loadedPages ?? 1,
  initialized: false,
};

const persistHomeState = () => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      HOME_STATE_KEY,
      JSON.stringify({
        filters: homeStateCache.filters,
        applied: homeStateCache.applied,
        sortType: homeStateCache.sortType,
        tab: homeStateCache.tab,
        scrollY: homeStateCache.scrollY,
        loadedPages: homeStateCache.loadedPages,
      })
    );
  } catch { }
};

const TabButton = ({ testid, icon: Icon, children, active, onClick }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-t-lg text-sm font-semibold transition-colors border-b-2 whitespace-nowrap shrink-0 ${active
      ? "border-primary text-primary bg-primary/5"
      : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
  >
    <Icon size={15} weight={active ? "fill" : "duotone"} />
    {children}
  </button>
);

export default function Home() {
  const { t } = useLang();
  const queryClient = useQueryClient();
  const { token } = useAuth();

  const [filters, setFilters] = useState(() => ({ ...homeStateCache.filters }));
  const [applied, setApplied] = useState(() => ({ ...homeStateCache.applied }));
  const [sortType, setSortType] = useState(() => homeStateCache.sortType);
  const [layout, setLayout] = useState(() => homeStateCache.layout);
  const [tab, setTab] = useState(() => homeStateCache.tab);

  const [showPackageModal, setShowPackageModal] = useState(false);
  const [reelsOpen, setReelsOpen] = useState(false);
  const [heatLevel, setHeatLevel] = useState(() => getCachedViewHeatLevel());

  const applyTimer = useRef(null);
  const isFirstLayoutRun = useRef(true);
  const isFirstTabRun = useRef(true);
  const scrollRestored = useRef(false);
  const restoreTargetPages = useRef(homeStateCache.loadedPages);

  function scrollDown(number) {
    window.scrollBy({
      top: number,
      left: 0,
      behavior: "smooth",
    });
  }

  function scrollWindow(number) {
    scrollDown(number);
  }

  useEffect(() => {
    if (!token && (tab === "favorites" || tab === "notes")) {
      setTab("web");
    }
  }, [token, tab]);

  useEffect(() => {
    if (!token && reelsOpen) {
      setReelsOpen(false);
    }
  }, [token, reelsOpen]);

  useEffect(() => {
    homeStateCache.filters = filters;
    persistHomeState();
  }, [filters]);

  useEffect(() => {
    homeStateCache.applied = applied;
    persistHomeState();
  }, [applied]);

  useEffect(() => {
    homeStateCache.sortType = sortType;
    persistHomeState();
  }, [sortType]);

  useEffect(() => {
    homeStateCache.layout = layout;
  }, [layout]);

  useEffect(() => {
    homeStateCache.tab = tab;
    persistHomeState();

    if (isFirstTabRun.current) {
      isFirstTabRun.current = false;
      return;
    }

    if (window.innerWidth <= 720) {
      setTimeout(() => {
        scrollWindow(400);
      }, 300);
    }
  }, [tab]);

  useEffect(() => {
    if (isFirstLayoutRun.current) {
      isFirstLayoutRun.current = false;
      return;
    }

    const mode = LAYOUT_TO_VIEW_MODE[layout];
    localStorage.setItem("viewMode", mode);

    if (getAccessToken()) {
      updateViewMode(mode).catch(() => { });
    }
  }, [layout]);

  useEffect(() => {
    clearTimeout(applyTimer.current);
    applyTimer.current = setTimeout(() => {
      setApplied({ ...filters });
    }, 300);

    return () => clearTimeout(applyTimer.current);
  }, [filters]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const apply = (m) => {
      setLayout((cur) => (m.matches && cur === "table" ? "grid" : cur));
    };
    apply(mq);
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  useEffect(() => {
    if (heatLevel) return;

    fetchPropertyViewHeatLevel()
      .then((data) => {
        if (!data) return;
        cacheViewHeatLevel(data);
        setHeatLevel(data);
      })
      .catch(() => { });
  }, [heatLevel]);

  const comboBody = useMemo(() => {
    const extra = { sortType, tab: TAB_TO_BACKEND_TAB[tab] ?? tab };

    if (tab === "popular" && heatLevel?.active != null) {
      extra.hot = heatLevel.active;
    }

    return toComboRequest(applied, extra);
  }, [applied, sortType, tab, heatLevel]);

  const {
    data,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["listing", tab, comboBody, Boolean(token)],

    queryFn: async ({ pageParam = null }) => {
      const response = await fetchPropertiesKeyset(
        PAGE_SIZE,
        pageParam,
        comboBody
      );
      return response;
    },

    initialPageParam: null,

    getNextPageParam: (last) => {
      if (!last || last.success === false) return undefined;
      const list = Array.isArray(last.data)
        ? last.data
        : Array.isArray(last.data?.data)
          ? last.data.data
          : [];

      return list.length === PAGE_SIZE
        ? last.nextCursor ?? last.data?.nextCursor ?? undefined
        : undefined;
    },

    maxPages: 10,
  });

  const hasNoPackage = useMemo(() => {
    if (!data?.pages || data.pages.length === 0) return false;

    return data.pages.some((page) => {
      if (!page) return false;
      return page.success === false || page.data?.success === false;
    });
  }, [data]);

  useEffect(() => {
    if (hasNoPackage) {
      setShowPackageModal(true);
    }
  }, [hasNoPackage]);

  const { ref: sentinelRef, inView } = useInView({ rootMargin: "300px 0px" });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const pageCount = data?.pages?.length;
    if (!pageCount) return;
    homeStateCache.loadedPages = pageCount;
    persistHomeState();
  }, [data]);

  useLayoutEffect(() => {
    if (scrollRestored.current) return;
    if (isLoading) return;

    const currentPages = data?.pages?.length ?? 0;
    const target = restoreTargetPages.current;

    if (currentPages < target && hasNextPage) {
      if (!isFetchingNextPage) {
        fetchNextPage();
      }
      return;
    }

    scrollRestored.current = true;

    const y = homeStateCache.scrollY || 0;
    if (y > 0) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo(0, y);
        });
      });
    }
  }, [isLoading, data, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const onScroll = () => {
      homeStateCache.scrollY = window.scrollY;
    };
    const onHide = () => persistHomeState();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", onHide);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", onHide);
      document.removeEventListener("visibilitychange", onHide);
    };
  }, []);

  const handleTabChange = (nextTab) => {
    if (nextTab === tab) return;
    setShowPackageModal(false);
    queryClient.removeQueries({ queryKey: ["listing"], exact: false });
    homeStateCache.loadedPages = 1;
    homeStateCache.scrollY = 0;
    restoreTargetPages.current = 1;
    scrollRestored.current = true;
    setTab(nextTab);
  };

  const onApply = () => {
    clearTimeout(applyTimer.current);
    setShowPackageModal(false);
    setApplied({ ...filters });
    queryClient.removeQueries({ queryKey: ["listing"], exact: false });
    homeStateCache.loadedPages = 1;
    homeStateCache.scrollY = 0;
    restoreTargetPages.current = 1;
    scrollRestored.current = true;
    persistHomeState();
  };

  const onReset = () => {
    clearTimeout(applyTimer.current);
    setShowPackageModal(false);
    const empty = { ...EMPTY_FILTERS };
    setFilters(empty);
    setApplied(empty);
    queryClient.removeQueries({ queryKey: ["listing"], exact: false });
    homeStateCache.loadedPages = 1;
    homeStateCache.scrollY = 0;
    restoreTargetPages.current = 1;
    scrollRestored.current = true;
    persistHomeState();
  };

  const handlePackageNo = () => {
    setShowPackageModal(false);
  };

  const handlePackageYes = () => {
    setShowPackageModal(false);
    window.location.href = "/payments";
  };

  const getPageItems = (page) => {
    if (!page || page.success === false) return [];
    if (Array.isArray(page.data)) return page.data;
    if (Array.isArray(page.data?.data)) return page.data.data;
    return [];
  };

  const items = useMemo(() => {
    const raw = data ? data.pages.flatMap((page) => getPageItems(page)) : [];

    if (tab === "favorites") {
      return [...raw].sort((a, b) => {
        const dateA = a.colorDate ? new Date(a.colorDate).getTime() : 0;
        const dateB = b.colorDate ? new Date(b.colorDate).getTime() : 0;
        return dateB - dateA;
      });
    }

    return raw;
  }, [data, tab]);

  return (
    <>
      <SEO
        title="Emlak.PRO - Daşınmaz Əmlak Elanları Platforması"
        description="Əmlak PRO ilə Azərbaycanda ev, mənzil, torpaq, ofis və obyektləri asanlıqla axtarın."
        canonical="https://emlak.pro/"
      />

      <div className="w-full px-4 lg:px-8 py-4">
        <h1 className="sr-only">
          Əmlak PRO — Azərbaycanda kirayə və satılıq daşınmaz əmlak elanları
        </h1>

        <div
          data-testid="tabs-row"
          className="flex items-center gap-1 border-b border-border/60 mb-3 overflow-x-auto overflow-y-hidden scrollbar-hidden"
        >
          <TabButton
            testid="tab-web"
            icon={Globe}
            active={tab === "web"}
            onClick={() => handleTabChange("web")}
          >
            <span className="text-[12px] sm:text-[15px]">Web</span>
          </TabButton>

          {token && (
            <TabButton
              testid="tab-favorites"
              icon={Star}
              active={tab === "favorites"}
              onClick={() => handleTabChange("favorites")}
            >
              <span className="text-[12px] sm:text-[15px]">Seçdiklərim</span>
            </TabButton>
          )}

          <TabButton
            testid="tab-popular"
            icon={Fire}
            active={tab === "popular"}
            onClick={() => handleTabChange("popular")}
          >
            <span className="text-[12px] sm:text-[15px]">Çox baxılanlar</span>
          </TabButton>

          {token && (
            <TabButton
              testid="tab-notes"
              icon={NotePencil}
              active={tab === "notes"}
              onClick={() => handleTabChange("notes")}
            >
              <span className="text-[12px] sm:text-[15px]">Qeydlərim</span>
            </TabButton>
          )}
        </div>

        <div className="mb-3">
          <HorizontalFilterBar
            filters={filters}
            setFilters={setFilters}
            onApply={onApply}
            onReset={onReset}
          />
        </div>

        <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
          <div className="flex items-center gap-1">
            <div className="flex items-center rounded-lg border border-border/60 bg-card p-1">
              <button
                type="button"
                onClick={() => setLayout("table")}
                className={`h-8 w-8 rounded-md flex items-center justify-center transition-colors ${layout === "table"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
                  }`}
              >
                <TableIcon size={14} weight="bold" />
              </button>

              <button
                type="button"
                onClick={() => setLayout("grid")}
                className={`h-8 w-8 rounded-md flex items-center justify-center transition-colors ${layout === "grid"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
                  }`}
              >
                <GridFour size={14} weight="bold" />
              </button>

              <button
                type="button"
                onClick={() => setLayout("list")}
                className={`h-8 w-8 rounded-md flex items-center justify-center transition-colors ${layout === "list"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
                  }`}
              >
                <Rows size={14} weight="bold" />
              </button>
            </div>

            {token && (
              <div className="rounded-lg border border-border/60 bg-card p-1">
                <button
                  type="button"
                  onClick={() => setReelsOpen(true)}
                  className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Film size={14} />
                </button>
              </div>
            )}
          </div>

          <Select value={sortType} onValueChange={setSortType}>
            <SelectTrigger className="w-[200px] h-9 rounded-lg bg-card text-sm">
              <SelectValue placeholder="Elanı sırala" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">{t("common.sortNewest")}</SelectItem>
              <SelectItem value="oldest">{t("common.sortOldest")}</SelectItem>
              <SelectItem value="price_asc">{t("common.sortPriceAsc")}</SelectItem>
              <SelectItem value="price_desc">{t("common.sortPriceDesc")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="h-96 rounded-xl bg-muted animate-pulse" />
        ) : hasNoPackage ? (
          <div className="flex flex-col items-center justify-center min-h-[350px] border border-border/60 bg-card rounded-2xl p-8 text-center my-4 shadow-sm">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/10 text-amber-500 mb-4">
              <WarningCircle size={40} weight="fill" />
            </div>
            <h3 className="text-lg font-bold text-foreground">
              Aktiv paketiniz yoxdur və ya bitmişdir
            </h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-md">
              Elan siyahısını görmək üçün aktiv paketinizin olması mütləqdir.
            </p>
            <button
              type="button"
              onClick={handlePackageYes}
              className="mt-5 px-6 h-10 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-all shadow"
            >
              Ödəniş səhifəsinə keçid et
            </button>
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={MagnifyingGlass}
            title={
              tab === "favorites"
                ? t("empty.favorites")
                : tab === "popular"
                  ? t("empty.popular")
                  : tab === "notes"
                    ? "Hələ heç bir qeydiniz yoxdur"
                    : t("common.noResults")
            }
            description={
              tab === "favorites"
                ? t("empty.favoritesDesc")
                : tab === "popular"
                  ? t("empty.popularDesc")
                  : tab === "notes"
                    ? "Elanlara qeyd əlavə etdikdən sonra burada görünəcək."
                    : t("common.noResultsDesc")
            }
            ctaLabel={tab === "web" ? t("common.reset") : t("empty.goBrowse")}
            ctaTo={undefined}
            onCtaClick={tab === "web" ? onReset : () => handleTabChange("web")}
            testid="home-empty-state"
          />
        ) : (
          <>
            {layout === "table" ? (
              <div>
                <PropertyTable
                  items={items}
                  filters={tab === "web" ? filters : undefined}
                  setFilters={tab === "web" ? setFilters : undefined}
                />
              </div>
            ) : (
              <div
                className={
                  layout === "grid"
                    ? "grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"
                    : "flex flex-col gap-3"
                }
              >
                {items.map((p) => (
                  <PropertyCard key={p.idProperty} property={p} layout={layout} />
                ))}
              </div>
            )}

            <div
              ref={sentinelRef}
              className="mt-6 flex items-center justify-center py-6 text-muted-foreground text-sm"
            >
              {isFetchingNextPage ? (
                <span className="inline-flex items-center gap-2">
                  <Spinner size={16} className="animate-spin" />
                  {t("common.loading")}
                </span>
              ) : hasNextPage ? (
                <span className="text-xs">Daha çox yüklə...</span>
              ) : (
                <span className="text-xs opacity-70">— Bütün elanlar göstərildi —</span>
              )}
            </div>
          </>
        )}
      </div>

      {showPackageModal && (
        <div
          className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/60 backdrop-blur-md p-4 animate-in fade-in duration-200"
          onClick={handlePackageNo}
        >
          <div
            className="relative w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl p-6 sm:p-8 animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={handlePackageNo}
              className="absolute top-4 right-4 rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <X size={20} />
            </button>

            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/10 text-amber-500 ring-8 ring-amber-500/5">
                <WarningCircle size={38} weight="fill" />
              </div>

              <h2 className="text-xl font-bold text-foreground">
                Aktiv paketiniz yoxdur və ya bitmişdir
              </h2>

              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Elanları görmək üçün aktiv paketinizin olması mütləqdir. Ödəniş səhifəsinə keçid edilsin?
              </p>

              <div className="mt-6 flex w-full gap-3">
                <button
                  type="button"
                  onClick={handlePackageNo}
                  className="flex-1 h-11 rounded-xl border border-border bg-background text-foreground font-semibold hover:bg-muted transition-colors"
                >
                  Xeyr
                </button>

                <button
                  type="button"
                  onClick={handlePackageYes}
                  className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors shadow-md"
                >
                  Bəli
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {token && reelsOpen && (
        <ReelsViewer
          tab={tab}
          sortType={sortType}
          token={token}
          onClose={() => setReelsOpen(false)}
        />
      )}

      <Footer handleTabChange={handleTabChange} />
    </>
  );
}